import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  NextPage,
} from "next";
import React, { useMemo } from "react";
import ArticlesList from "../components/articles-list";
import CategoriesList from "../components/categories-list";
import StandardLayout from "../components/layout.standard";
import loadCategories, { Section } from "../lib/load-categories";
import loadPopularArticles, { Article } from "../lib/load-popular-articles";
import styles from "../styles/general.module.css";
import type { AppSettings } from "../lib/get-app-settings";
import getBaseUrl from "../lib/get-base-url";
import { ForbiddenError, NotFoundError, UnauthorizedError } from "../lib/api";
import {
  THCSessionDetails,
  getSessionDetailsFromRequest,
  saveSessionDetailsToLocalStorage,
} from "../lib/hc-session";
import getObjectByAlias from "../lib/get-object-by-alias";
import t from "../texts/t";
import type { THelpCenterStandardLayout } from "../lib/load-company-settings";
import getRedirectUrl from "../lib/get-redirect-url";

export type TCompanyStandardPageProps = {
  appSettings: AppSettings & { layout: THelpCenterStandardLayout };
  popularArticles: Article[];
  categories: Section[];
  hcSessionDetails: THCSessionDetails;
};

const CompanyStandardPage: NextPage<TCompanyStandardPageProps> = ({
  appSettings,
  popularArticles,
  categories,
  hcSessionDetails,
}) => {
  const baseUrl = useMemo(() => getBaseUrl(appSettings), [appSettings]);

  React.useEffect(() => {
    if (hcSessionDetails && appSettings) {
      saveSessionDetailsToLocalStorage(appSettings, hcSessionDetails);
    }
  }, [hcSessionDetails, appSettings]);

  return (
    <StandardLayout appSettings={appSettings} hasTitle>
      {categories.length || popularArticles.length ? (
        <React.Fragment>
          {categories.length ? (
            <React.Fragment>
              <div
                className={styles.companyPageTitle}
                lang={appSettings.language}
              >
                {t("Browse by category", appSettings.language)}
              </div>
              <CategoriesList
                className={styles.companyPageContent}
                categories={categories}
                baseUrl={baseUrl}
                layout={appSettings.layout}
              />
            </React.Fragment>
          ) : null}
          {popularArticles.length ? (
            <React.Fragment>
              <div
                className={styles.companyPageTitle}
                lang={appSettings.language}
              >
                {t("Popular articles", appSettings.language)}
              </div>
              <ArticlesList
                className={styles.companyPageContent}
                articles={popularArticles}
                baseUrl={baseUrl}
              />
            </React.Fragment>
          ) : null}
        </React.Fragment>
      ) : (
        <p className={styles.emptyListMessage}>
          {t("Whoops! Page not found!", appSettings.language)}
        </p>
      )}
    </StandardLayout>
  );
};

export default CompanyStandardPage;

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
  appSettings: AppSettings & { layout: THelpCenterStandardLayout }
): Promise<ReturnType<GetServerSideProps<TCompanyStandardPageProps>>> => {
  const pathSansSearchParams = context.resolvedUrl.split("?")[0];
  if (pathSansSearchParams !== "/") {
    try {
      const object = await getObjectByAlias(
        appSettings.companyId,
        context.resolvedUrl,
        context
      );

      if (object) {
        return {
          redirect: {
            statusCode: 302,
            destination: getRedirectUrl(object.path, context),
          },
        };
      }
    } catch (error: any) {
      // Fall through
    }
  }

  // initialize hc-session-details
  const hcSessionDetails = await getSessionDetailsFromRequest(
    context,
    appSettings
  );

  try {
    const [allCategories = [], popularArticles] = await Promise.all([
      loadCategories(appSettings.slug, context),
      loadPopularArticles(appSettings.slug, context),
    ]);

    const props: TCompanyStandardPageProps = {
      appSettings,
      popularArticles,
      categories: allCategories.filter((category) => !category.parentId),
      hcSessionDetails,
    };

    return { props };
  } catch (error: any) {
    console.error(error);
    if (
      error instanceof UnauthorizedError ||
      error instanceof ForbiddenError ||
      error instanceof NotFoundError
    ) {
      throw error;
    }
  }

  return {
    redirect: { statusCode: 302, destination: getRedirectUrl("/", context) },
  };
};
